<template>
  <v-card>
    <v-card-title> {{ $t("common.edit") + ": " + cohortData.title }}</v-card-title>
    <v-card-text>
      <v-text-field
        v-model="cohortData.title"
        variant="outlined"
        label="Title"
      />

      <v-select
        v-model="currentSchoolYear"
        :items="[6,7,8,9,10,11,12]"
        label="Current Grade"
        variant="outlined"
      />

      <v-select
        v-model="cohortData.exitYear"
        :items="academicYearItems"
        variant="outlined"
        label="Exit / Matric Year"/>

      <v-switch
        color="primary"
        v-model="cohortData.active"
        label="Is Active"
      />

    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn-secondary
        @click="$emit('close')"
      >
        {{ $t("buttons.cancel") }}
      </v-btn-secondary>
      <v-btn-primary
        @click="editCohort"
      >
        {{ $t("buttons.confirm") }}
      </v-btn-primary>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {onMounted, PropType, ref, watch} from "vue";
import {Cohort, isYapiError, YapiError} from "@YenzaCT/sdk";
import {useGlobalStore} from "@/store";
import yapi from "@/lib/yapi";

const store = useGlobalStore();
const currentSchoolYear = ref<number>();

const props = defineProps({
  cohort: {
    type: Object as PropType<Cohort>,
    required: true
  }
});

import {computed} from "vue";
import {getSchoolYearFromExitYear} from "@/lib/util";

const academicYearItems = computed(() => {
  const currentYear = new Date().getFullYear();
  const startYear = 2020;
  const endYear = currentYear + 7;
  return Array.from({length: endYear - startYear + 1}, (_, i) => (endYear - i));
});

const cohortData = ref<Cohort>({...props.cohort});

const emits = defineEmits<{
  (e: "close"): void;
}
>();

onMounted(() => {
  setCurrentSchoolYear();
});

watch(() => cohortData.value.exitYear, (newExitYear) => {
  if (newExitYear) {
    setCurrentSchoolYear();
  }
});

watch(() => currentSchoolYear.value, (newSchoolYear) => {
  if (newSchoolYear) {
    const currentYear = new Date().getFullYear();
    const newExitYear = currentYear + (12 - newSchoolYear);

    if (cohortData.value.exitYear !== newExitYear) {
      cohortData.value.exitYear = newExitYear;
    }
  }
});

function setCurrentSchoolYear() {
  if (cohortData.value.exitYear)
    currentSchoolYear.value = getSchoolYearFromExitYear(cohortData.value.exitYear);
}

const editCohort = async () => {
  try {
    store.networkBusy = true;
    await yapi.admin.cohort.edit(props.cohort._id as string, {
      title: cohortData.value.title,
      active: cohortData.value.active,
      exitYear: cohortData.value.exitYear,
    });
    emits("close");
    store.showSnackbar("Cohort updated successfully");
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
};

</script>

<style scoped>

</style>
